import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ImagePlus, Upload, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

export const PhotoSubmitForm = ({ onSubmitted }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Immagine troppo grande (max 10MB)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Aggiungi una foto"); return; }
    if (!name.trim()) { toast.error("Inserisci il tuo nome"); return; }
    if (!consent) { toast.error("Devi acconsentire alla pubblicazione"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      fd.append("caption", caption.trim());
      fd.append("consent", "true");
      await api.submitPhoto(fd);
      toast.success("Foto inviata! Sarà pubblicata dopo l'approvazione del locale. Grazie!");
      setFile(null); setPreview(null); setName(""); setCaption(""); setConsent(false);
      if (inputRef.current) inputRef.current.value = "";
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      data-testid="photo-submit-form"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="rounded-2xl bg-black/50 backdrop-blur-xl ring-1 ring-white/10 p-6 sm:p-8 space-y-4 shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
    >
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="photo-file-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid="photo-pick-btn"
        className="w-full aspect-video rounded-xl ring-1 ring-dashed ring-white/20 bg-white/[0.02] hover:ring-primary/50 transition-colors flex items-center justify-center overflow-hidden"
      >
        {preview ? (
          <img src={preview} alt="Anteprima" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center text-muted-foreground">
            <ImagePlus className="w-8 h-8 mb-2 text-primary" />
            <span className="text-sm">Tocca per scegliere una foto</span>
          </div>
        )}
      </button>

      <Input data-testid="photo-name-input" placeholder="Il tuo nome" value={name} onChange={(e) => setName(e.target.value)}
        className="h-12 bg-zinc-900 border-zinc-800 focus-visible:ring-primary" />
      <Textarea data-testid="photo-caption-input" placeholder="Didascalia (opzionale): cosa stai bevendo, l'occasione..." value={caption}
        onChange={(e) => setCaption(e.target.value)} className="bg-zinc-900 border-zinc-800 focus-visible:ring-primary min-h-[70px]" />

      <label className="flex items-start gap-3 cursor-pointer text-sm text-muted-foreground">
        <Checkbox data-testid="photo-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <span>Acconsento alla pubblicazione della foto sul sito e sui canali di MI.RO'.</span>
      </label>

      <Button data-testid="photo-submit-btn" type="submit" disabled={busy}
        className="w-full h-12 rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold transition-colors duration-300">
        {busy ? <><Upload className="w-4 h-4 mr-2 animate-pulse" /> Invio...</> : <><Check className="w-4 h-4 mr-2" /> Invia la tua foto</>}
      </Button>
    </motion.form>
  );
};
